// ═══════════════════════════════════════════════════════════════════════════
// AI-callable safe-contour guard — VIEW+SPACE domain (CC-2c sub-arm 3)
// ═══════════════════════════════════════════════════════════════════════════
//
// Mirrors the draw-modify `assertAiCallableDrawAction` (drawModifyGuard.ts): maps
// every view+space AI action to the user-facing registry command it targets,
// then reuses the CC-2a generic `assertAiCallableCommand` to fail CLOSED when
// that target is not `aiCallable: true` (or is absent from the registry).
//
// The guard is ACTION-level, not literal-level: the registry descriptor the
// action targets is checked (e.g. CREATE_VIEWPORT → "MVIEW"), never the internal
// EditorCommand literal the translator emits (`geometry/create-viewport`,
// `shell/set-layout-context`, `shell/rename-space`). This is the same rule the
// block/entity/draw guards document — the AI-callable surface is the COMMAND the
// AI is asking for, while the emitted shell/geometry literals are internal
// mechanics of the safe contour (MSPACE/PSPACE/LAYOUT descriptors are status
// "stub" with empty `commandTypeLiterals`, yet `aiCallable: true`; the literal
// they dispatch is plumbing, exactly like the block-edit session steps).

import {
  ALL_COMMAND_DESCRIPTORS,
  type CommandDescriptor,
  type CommandRegistryId,
} from "@dxf-ai-editor/command-registry";
import {
  parseColorCommandValueStrict,
  type EntityColor,
} from "@dxf-ai-editor/drawing-model";

import { assertAiCallableCommand } from "./aiCallableGuard";
import type { AiDispatchContext } from "./blockDispatcherShared";
import type { ViewSpaceAiCommand } from "./viewSpaceSchemas";

/**
 * Typed error thrown when a VIEW+SPACE AI action cannot be satisfied against the
 * current document context — distinct from {@link NonAiCallableCommandError}
 * (registry aiCallable flag) and `AiCommandValidationError` (schema shape). It
 * is the RUNTIME-STATE rejection: MSPACE / PSPACE with no paper-space layout to
 * switch into, or MVIEW targeting a non-paper-space `spaceId` (model / orphan).
 * Thrown BEFORE any EditorCommand is produced, so the safe contour never opens a
 * history batch for it (fail-closed). `instanceof`-narrowable for the AI
 * transcript / audit trail.
 */
export class ViewSpaceContextError extends Error {
  readonly reason: "no-active-layout" | "not-paper-space-layout";
  /** The offending space id (MVIEW) when the rejection is about one. */
  readonly spaceId?: string;

  constructor(
    reason: "no-active-layout" | "not-paper-space-layout",
    spaceId?: string,
  ) {
    super(
      reason === "no-active-layout"
        ? "AI safe contour: no paper-space layout is available to switch into; refusing to dispatch MSPACE/PSPACE (fail-closed). MSPACE/PSPACE require a layout."
        : `AI safe contour: target spaceId "${spaceId ?? ""}" is not a paper-space layout (model / orphan ids are rejected — MVIEW and LAYOUT Set are layout-only); refusing to dispatch (fail-closed).`,
    );
    this.name = "ViewSpaceContextError";
    this.reason = reason;
    if (spaceId !== undefined) this.spaceId = spaceId;
  }
}

/**
 * Typed error thrown when the AI `SET_CURRENT_COLOR` action carries a colour
 * VALUE the strict validator rejects (e.g. `"999"` — an out-of-range ACI the
 * export would emit as an invalid `$CECOLOR=62=999`; or `"blu"` — an unknown
 * keyword the permissive `coerceColor` would silently reset to ByLayer). The AI
 * is a LESS-trusted contour (CLAUDE.md "AI drafting … может только … через
 * безопасный контур"), so it must enforce the SAME strict accept-list the human
 * COLOR command does (CECOLOR FINDING 2) rather than coercing to a fallback —
 * fail-closed BEFORE any `header/set-cecolor` EditorCommand is produced, so the
 * safe contour never writes an invalid current colour. `instanceof`-narrowable
 * for the AI transcript / audit trail.
 */
export class AiColorValidationError extends Error {
  /** The offending raw colour string the AI supplied. */
  readonly value: string;

  constructor(value: string) {
    super(
      `AI safe contour: SET_CURRENT_COLOR value "${value}" is not a valid colour ` +
        `(accepted: ByLayer / ByBlock / ACI 1..255 / #rrggbb / "R,G,B"); ` +
        `refusing to dispatch header/set-cecolor (fail-closed). AutoCAD: "Invalid color."`,
    );
    this.name = "AiColorValidationError";
    this.value = value;
  }
}

/**
 * STRICTLY validate an AI-supplied colour VALUE for `SET_CURRENT_COLOR`, reusing
 * the ONE-truth strict validator {@link parseColorCommandValueStrict} the human
 * COLOR command enforces (drawing-model — NOT the permissive `coerceColor`).
 * Returns the typed {@link EntityColor} on a valid value, or throws
 * {@link AiColorValidationError} (fail-closed) so the AI cannot store an
 * out-of-range `$CECOLOR` (62=999) or silently wipe the current colour to
 * ByLayer on an unknown keyword. Thrown BEFORE any EditorCommand is produced.
 */
export function resolveAiCurrentColor(value: string): EntityColor {
  const color = parseColorCommandValueStrict(value);
  if (color === null) throw new AiColorValidationError(value);
  return color;
}

/**
 * Resolve the paper-space layout MSPACE / PSPACE should activate, mirroring the
 * CLI MSPACE handler (editorStore: model → first layout; already-on-a-layout →
 * stay on it). Returns `null` when no paper-space layout exists, so the caller
 * can fail closed. Single source of truth: the `paperSpaceLayoutIds` context
 * list (DrawingSpace `kind === "layout"`, tab order).
 */
export function resolveActivationLayoutId(
  context: AiDispatchContext,
): string | null {
  const layouts = context.paperSpaceLayoutIds ?? [];
  const active = context.activeSpaceId;
  // Already focused on a paper-space layout → switch context in place (MSPACE
  // does not jump to a different tab when one is already active).
  if (active !== undefined && active !== "model" && layouts.includes(active)) {
    return active;
  }
  // From model space (or an unknown/non-layout active space) move to the FIRST
  // paper-space layout — the CLI MSPACE model→layout-1 behaviour, generalised to
  // the real first layout id rather than a hard-coded "layout-1".
  return layouts[0] ?? null;
}

/**
 * Whether `spaceId` is a known paper-space layout per the context list. Used by
 * the MVIEW translator to reject model / orphan targets at the AI boundary. When
 * the context does not supply the list (`undefined`) the check is SKIPPED (the
 * reducer remains the fail-closed safety net) — distinct from an EMPTY list,
 * which means "document has no layouts" → every spaceId is rejected.
 */
export function isKnownPaperSpaceLayout(
  spaceId: string,
  context: AiDispatchContext,
): boolean {
  return (context.paperSpaceLayoutIds ?? []).includes(spaceId);
}

/**
 * Fail-closed assertion that an AI-supplied `spaceId` targets a real paper-space
 * layout. Shared one-truth guard for the layout-only AI actions (MVIEW creates a
 * viewport on a paper layout; LAYOUT Set makes a paper layout current) — the
 * Model tab and orphan ids are rejected. When the context omits the layout list
 * (`paperSpaceLayoutIds === undefined`) the check is SKIPPED so the reducer
 * remains the safety net; an EMPTY list rejects every id ("document has no
 * layouts"). Throws {@link ViewSpaceContextError} with reason
 * `not-paper-space-layout` BEFORE any EditorCommand is produced.
 */
export function assertPaperSpaceLayoutTarget(
  spaceId: string,
  context: AiDispatchContext,
): void {
  if (
    context.paperSpaceLayoutIds !== undefined &&
    !isKnownPaperSpaceLayout(spaceId, context)
  ) {
    throw new ViewSpaceContextError("not-paper-space-layout", spaceId);
  }
}

/**
 * Maps every view+space AI action to the registry command it targets. The guard
 * resolves this id and checks its `aiCallable` flag, so an action whose target
 * is ever flipped to `aiCallable: false` fails closed automatically.
 *
 * `Record<ViewSpaceAiCommand["action"], …>` forces exhaustiveness: adding a new
 * view+space action without declaring its target command is a compile error.
 * Each target is `aiCallable: true` in the registry today (layout.ts) and listed
 * AI-callable in ТЗ §10.1.
 *
 * Mapping rationale (see command-registry/commands/layout.ts):
 *   • CREATE_VIEWPORT → MVIEW (creates a VIEWPORT entity on a paper layout).
 *   • SWITCH_TO_MODEL_SPACE → MSPACE; SWITCH_TO_PAPER_SPACE → PSPACE — both
 *     switch the layout context (their `shell/set-layout-context` literal is
 *     internal plumbing; the AI-callable surface is the MSPACE/PSPACE command).
 *   • SET_LAYOUT → LAYOUT — the SET sub-command (make a paper-space layout
 *     current; dispatches the canonical `shell/activate-space`).
 *   • RENAME_LAYOUT → LAYOUT — the RENAME sub-command (`shell/rename-space`).
 *   • SET_CURRENT_COLOR → COLOR — sets CECOLOR (`header/set-cecolor`); the COLOR
 *     registry descriptor is `aiCallable: true`.
 */
export const VIEW_SPACE_AI_ACTION_TARGET_COMMAND: Record<
  ViewSpaceAiCommand["action"],
  CommandRegistryId
> = {
  CREATE_VIEWPORT: "MVIEW",
  SWITCH_TO_MODEL_SPACE: "MSPACE",
  SWITCH_TO_PAPER_SPACE: "PSPACE",
  SET_LAYOUT: "LAYOUT",
  RENAME_LAYOUT: "LAYOUT",
  SET_CURRENT_COLOR: "COLOR",
};

/**
 * Guard a single view+space AI action: resolves its target registry command and
 * asserts AI-callability. Throws `NonAiCallableCommandError` when the target is
 * not AI-callable (fail-closed). Called by `translateViewSpaceCommand` before
 * any EditorCommand is produced.
 */
export function assertAiCallableViewSpaceAction(
  action: ViewSpaceAiCommand["action"],
  descriptors: readonly CommandDescriptor[] = ALL_COMMAND_DESCRIPTORS,
): void {
  assertAiCallableCommand(
    VIEW_SPACE_AI_ACTION_TARGET_COMMAND[action],
    descriptors,
  );
}
